import type { BotStatusDto } from '@rhcst/shared';
import { TallyLight } from './TallyLight.js';
import type { Posture, LampState } from './TallyLight.js';
import { shortAddress, relativeAge } from '../lib/format.js';

/**
 * The interlock strip.
 *
 * INVARIANT 5 (docs/DESIGN_HANDOFF.md §4.5): present on every authenticated
 * screen. Never a page, a modal, or a collapsible.
 *
 * Whether this system can spend money is not a destination — it is ambient
 * state, and an operator must never have to navigate somewhere to find out. So
 * it lives in the shell, above the content, on every route.
 *
 * Reading order left to right is deliberate, coarsest first: the ARMED/SAFED
 * word, then the kill switch, then the four interlock lamps, then the wallet,
 * then scanner liveness pushed to the far right. An operator glancing at it gets
 * the answer from the first element and only reads on if they need detail.
 *
 * `null` status means no telemetry. The strip says so rather than showing a
 * safe-looking default: an instrument that invents a reassuring reading is worse
 * than one that admits it has none.
 */

export interface Interlocks {
  killSwitch: boolean;
  tradingEnabled: boolean;
  botWalletEnabled: boolean;
}

/**
 * The composite posture.
 *
 * Armed requires every interlock to permit spending. Any one of them closed
 * means safed — this is an AND, not a majority, because each interlock alone is
 * sufficient to stop a trade.
 */
export function derivePosture(interlocks: Interlocks | null): Posture {
  if (!interlocks) return 'unlit';
  const armed =
    !interlocks.killSwitch && interlocks.tradingEnabled && interlocks.botWalletEnabled;
  return armed ? 'armed' : 'safed';
}

interface Lamp {
  name: string;
  state: LampState;
  reading: string;
}

/**
 * One lamp per interlock.
 *
 * A lamp lights **armed** only when its interlock permits spending, so a row of
 * cool lamps is the safe reading rather than a broken one — the convention of a
 * machine that shows nothing until it is live.
 *
 * The kill switch is inverted relative to the others and is the one place a lamp
 * shows `alarm`: an engaged kill switch is not merely "not permitting", it is an
 * operator having deliberately stopped the machine, and that deserves to look
 * different from a flag that was simply never turned on.
 */
export function toLamps(interlocks: Interlocks | null, walletConfigured: boolean): Lamp[] {
  if (!interlocks) {
    return [
      { name: 'Kill switch', state: 'unlit', reading: 'no reading' },
      { name: 'Trading', state: 'unlit', reading: 'no reading' },
      { name: 'Hot wallet', state: 'unlit', reading: 'no reading' },
      { name: 'Wallet key', state: 'unlit', reading: 'no reading' },
    ];
  }

  return [
    {
      name: 'Kill switch',
      state: interlocks.killSwitch ? 'alarm' : 'armed',
      reading: interlocks.killSwitch ? 'engaged — nothing signs' : 'released',
    },
    {
      name: 'Trading',
      state: interlocks.tradingEnabled ? 'armed' : 'safed',
      reading: interlocks.tradingEnabled ? 'enabled' : 'disabled',
    },
    {
      name: 'Hot wallet',
      state: interlocks.botWalletEnabled ? 'armed' : 'safed',
      reading: interlocks.botWalletEnabled ? 'enabled' : 'disabled',
    },
    {
      name: 'Wallet key',
      state: walletConfigured ? 'armed' : 'safed',
      reading: walletConfigured ? 'present' : 'not configured',
    },
  ];
}

export interface InterlockStripProps {
  status: BotStatusDto | null;
  stale: boolean;
}

export function InterlockStrip({ status, stale }: InterlockStripProps) {
  const interlocks: Interlocks | null = status
    ? {
        killSwitch: status.interlocks.kill_switch_engaged,
        tradingEnabled: status.interlocks.trading_enabled,
        botWalletEnabled: status.interlocks.bot_wallet_enabled,
      }
    : null;

  const posture = derivePosture(interlocks);
  const armed = posture === 'armed';
  const lamps = toLamps(interlocks, status?.wallet.key_configured ?? false);

  const killEngaged = status?.kill_switch.engaged;
  const walletAddress = status?.wallet.address ?? null;
  const scannerOn = status?.scanner.enabled ?? false;
  const lastScan = status?.scanner.last_scanned_at ?? null;

  return (
    <div
      className="strip"
      role="status"
      aria-live="polite"
      aria-label="Trading interlock status"
    >
      {/* The one-glance answer. Breathes when armed. */}
      <span className="strip-state" data-armed={posture === 'unlit' ? 'unknown' : armed}>
        <span className="strip-dot" aria-hidden="true" />
        <span className="strip-word">
          {posture === 'unlit' ? 'No reading' : armed ? 'Armed' : 'Safed'}
        </span>
      </span>

      <span className="strip-divider" aria-hidden="true" />

      <span className="strip-group">
        <span className="label label-dim">Kill switch</span>
        <span
          className="strip-value"
          data-armed={killEngaged === undefined ? 'unknown' : !killEngaged}
        >
          {killEngaged === undefined ? '—' : killEngaged ? 'ON' : 'OFF'}
        </span>
      </span>

      <span className="strip-group">
        <span className="label label-dim">Interlocks</span>
        <span className="strip-lamps">
          {lamps.map((lamp) => (
            <TallyLight
              key={lamp.name}
              state={lamp.state}
              label={`${lamp.name}: ${lamp.reading}`}
            />
          ))}
        </span>
      </span>

      <span className="strip-group">
        <span className="label label-dim">Wallet</span>
        <span className="data-xs secondary">
          {walletAddress ? shortAddress(walletAddress) : 'none configured'}
        </span>
      </span>

      {/* Scanner liveness is neutral grey, never armed or safed: the scanner
          running is an operational fact, not a money-can-move fact. Colouring
          it with the temperature palette would make the temperature cue lie. */}
      <span className="strip-live">
        <span className="live-dot" data-live={scannerOn} aria-hidden="true" />
        <span className="data-xs muted">
          {!status
            ? 'status unavailable'
            : scannerOn
              ? lastScan
                ? `scanning · ${relativeAge(lastScan)} ago`
                : 'scanning · no tick yet'
              : 'scanner stopped'}
          {stale ? ' · stale' : ''}
        </span>
      </span>
    </div>
  );
}
